import { Breadcrumbs } from '@/components/ui/Breadcrumbs';
import { Skeleton } from '@/components/ui/Skeleton';
import { useSuperadmin } from '@/hooks/useSuperadmin';
import { useAppSelector } from '@/store/hooks';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import GlobalSearch from './GlobalSearch';
import Header from './Header';
import Sidebar from './Sidebar';

export default function DashboardLayout() {
  const { isAuthenticated, isLoading, organizer, user } = useAppSelector((state) => state.auth);
  const location = useLocation();
  const isSuperadmin = useSuperadmin();

  // Mostrar loading mientras se verifica autenticación o rol de superadmin
  if (isLoading || (isAuthenticated && isSuperadmin === null)) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        {/* Header skeleton móvil */}
        <div className="lg:hidden h-14 bg-white border-b border-gray-200 px-4 flex items-center justify-between">
          <Skeleton className="h-8 w-32" />
          <Skeleton className="h-8 w-8 rounded-lg" />
        </div>

        <div className="flex flex-1">
          {/* Sidebar skeleton */}
          <div className="hidden lg:flex flex-col w-64 bg-white border-r border-gray-200">
            <div className="p-4 border-b border-gray-100">
              <div className="flex items-center gap-2 mb-4">
                <Skeleton className="h-8 w-8 rounded-lg" />
                <Skeleton className="h-6 w-24" />
              </div>
              <Skeleton className="h-14 w-full rounded-xl" />
            </div>
            <div className="p-4">
              <Skeleton className="h-10 w-full rounded-xl mb-4" />
              <div className="space-y-2">
                {[1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} className="h-10 w-full rounded-xl" />
                ))}
              </div>
            </div>
          </div>

          {/* Main content skeleton */}
          <main className="flex-1 p-6 lg:p-8">
            <div className="max-w-7xl mx-auto space-y-6">
              <Skeleton className="h-8 w-48" />
              <Skeleton className="h-4 w-64" />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-white rounded-xl border border-gray-200 p-6">
                    <Skeleton className="h-4 w-24 mb-4" />
                    <Skeleton className="h-8 w-16 mb-2" />
                    <Skeleton className="h-3 w-20" />
                  </div>
                ))}
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  // Si no está autenticado, redirigir a login
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  // Si es superadmin (SOLO gestiona usuarios)
  if (isSuperadmin) {
    if (location.pathname === '/dashboard' || !location.pathname.startsWith('/dashboard/users')) {
      return <Navigate to="/dashboard/users" replace />;
    }
  } else {
    // Si NO es superadmin, debe ser organizador
    if (!organizer && isSuperadmin === false) {
      return <Navigate to="/login" replace />;
    }
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#FAFAFA]">
      <GlobalSearch />
      
      {/* Header solo visible en móvil */}
      <Header />

      <div className="flex flex-1">
        {/* Sidebar - solo visible en desktop */}
        <Sidebar />

        {/* Main Content */}
        <main className="flex-1 overflow-x-hidden">
          {/* Header superior estilo GeoTracking */}
          <PageHeader />
          
          {/* Contenido de la página */}
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

// Header superior estilo GeoTracking con título y breadcrumbs
function PageHeader() {
  const location = useLocation();
  
  // No mostrar en la raíz del dashboard
  if (location.pathname === '/dashboard') {
    return null;
  }

  // Obtener el título de la página basado en la ruta
  const pageTitle = getPageTitle(location.pathname);

  return (
    <div className="bg-white border-b border-gray-200 px-4 sm:px-6 lg:px-8 py-5">
      <div className="max-w-7xl mx-auto">
        {/* Contenedor flex para título y acciones */}
        <div className="flex items-start justify-between">
          {/* Lado izquierdo: Título y Breadcrumbs */}
          <div>
            {/* Título grande */}
            <h1 className="text-[28px] font-semibold text-gray-900 leading-tight">{pageTitle}</h1>
            
            {/* Breadcrumbs debajo del título */}
            <div className="mt-1">
              <Breadcrumbs />
            </div>
          </div>
          
          {/* Lado derecho: Acciones (botones cuadrados) */}
          <div className="flex items-center gap-2">
            <button
              className="flex items-center justify-center w-10 h-10 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 transition-colors"
              title="Exportar"
            >
              <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
              </svg>
            </button>
            <button
              className="flex items-center justify-center w-10 h-10 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 transition-colors"
              title="Más opciones"
            >
              <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 12.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 18.75a.75.75 0 110-1.5.75.75 0 010 1.5z" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Función para obtener el título de la página
function getPageTitle(pathname: string): string {
  const segments = pathname.split('/').filter(Boolean);
  
  const titleMap: Record<string, string> = {
    events: 'Mis Eventos',
    new: 'Crear Evento',
    edit: 'Configuración',
    map: 'Mapa de Asientos',
    analytics: 'Analíticas',
    attendees: 'Asistentes',
    checkin: 'Check-in',
    payments: 'Pagos Pendientes',
    users: 'Users',
    admins: 'Administrators',
    reports: 'Reportes',
    'work-orders': 'Work Orders',
    technicians: 'Technicians',
    customers: 'Customers',
    subscriptions: 'Subscriptions',
    devices: 'Devices & Inventory',
    'users-list': 'Users',
  };

  const lastSegment = segments[segments.length - 1];
  
  if (lastSegment && lastSegment.match(/^[0-9a-f-]{36}$/i)) {
    return 'Dashboard del Evento';
  }

  return titleMap[lastSegment] || capitalize(lastSegment);
}

function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}
